import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, X, User, ChevronLeft, ChevronRight } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

function UsersWhoWantToSubscribe() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [confirmAction, setConfirmAction] = useState(null);
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  
  const navigate = useNavigate();

  const { user } = useSelector(state => state.auth);
  const isAdmin = user?.role === 'admin';

  //OBTENER USUARIOS QUE QUIEREN SUSCRIBIRSE A UN PLAN
  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔐 Token enviado:', token);
      
      // Obtener las suscripciones
      const subscriptionsResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:4005'}/api/v1/userSubscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Crear un mapa de usuarios por ID para búsqueda rápida
      const usersMap = {};
      
      // Obtener todos los usuarios usando paginación para asegurar que obtenemos todos
      let hasMoreUsers = true;
      let currentPage = 1;
      const limit = 50; // Aumentar el límite para reducir llamadas API
      
      while (hasMoreUsers) {
        const usersResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:4005'}/api/v1/users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: {
            page: currentPage,
            limit: limit
          }
        });
        
        // Verificar la estructura de la respuesta
        if (usersResponse.data && usersResponse.data.data && Array.isArray(usersResponse.data.data)) {
          // Añadir los usuarios al mapa
          usersResponse.data.data.forEach(user => {
            usersMap[user.id] = user;
          });
          
          // Verificar si hay más páginas
          if (currentPage < usersResponse.data.totalPages) {
            currentPage++;
          } else {
            hasMoreUsers = false;
          }
        } else if (Array.isArray(usersResponse.data)) {
          // Para APIs que no usan formato paginado sino que devuelven directamente un array
          usersResponse.data.forEach(user => {
            usersMap[user.id] = user;
          });
          hasMoreUsers = false; // Terminar, ya que no hay paginación
        } else {
          console.error('Formato de respuesta de usuarios inesperado:', usersResponse.data);
          hasMoreUsers = false;
        }
      }
      
      // Enriquecer los datos de suscripción con información del usuario
      const enrichedPlans = subscriptionsResponse.data.map(plan => {
        const user = usersMap[plan.userId];
        
        // Log para depuración
        if (!user) {
          console.warn(`⚠️ No se encontró información para el usuario con ID: ${plan.userId}`);
        }
        
        return {
          ...plan,
          userName: user?.name || `ID: ${plan.userId}`,
          userEmail: user?.email || 'Sin email',
          userRole: user?.role || 'visitant'
        };
      });
      
      setPlans(enrichedPlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      window.alert('Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/unauthorized');
      return;
    }
    fetchPlans();
  }, [isAdmin, navigate]);

  // Mostrar diálogo de confirmación para acciones que requieren confirmación
  const showConfirmationDialog = (action, requestId, message) => {
    setConfirmAction({
      type: action,
      requestId,
      message
    });
  };

  // Ejecutar acción después de confirmar
  const executeAction = async () => {
    if (!confirmAction) return;
    
    const { type, requestId } = confirmAction;
    setProcessing(true);
    
    try {
      switch (type) {
        case 'approve':
          await handleApprove(requestId);
          break;
        case 'reject':
          await handleReject(requestId);
          break;
        default:
          break;
      }
    } finally {
      setProcessing(false);
      setConfirmAction(null);
    }
  };

  //ACEPTAR SOLICITUD
  const handleApprove = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      console.log('📤 Aprobando solicitud con ID:', requestId);
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:4005'}/api/v1/userSubscriptions/approve/${requestId}`;
      
      const response = await axios.put(
        url,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('✅ Respuesta del backend:', response.data);
      window.alert('Solicitud aprobada correctamente');
      
      // Actualizar estado local
      setPlans(plans.map(plan =>
        plan.id === requestId ? { ...plan, status: 'active' } : plan
      ));
    } catch (error) {
      console.error('❌ Error al aprobar solicitud:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      window.alert('Error al aprobar la solicitud');
    }
  };

  // Rechazar solicitud
  const handleReject = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:4005'}/api/v1/userSubscriptions/reject/${requestId}`;
      
      await axios.put(url, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      window.alert('Solicitud rechazada correctamente');
      
      // Actualizar estado local
      setPlans(plans.map(plan =>
        plan.id === requestId ? { ...plan, status: 'rejected' } : plan
      ));
    } catch (error) {
      console.error('❌ Error al rechazar solicitud:', error);
      window.alert('Error al rechazar la solicitud');
    }
  };

  // Filtrar planes según la pestaña activa
  const filteredPlans = plans.filter(plan => {
    switch (activeTab) {
      case 'active':
        return plan.status === 'active';
      case 'pending':
        return plan.status === 'pending';
      case 'rejected':
        return plan.status === 'rejected';
      default:
        return true; // 'all' muestra todos
    }
  });

  // Paginación de los resultados filtrados
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPlans.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPlans.length / itemsPerPage);
  
  // Función para cambiar de página
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Función para ir a la página anterior
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Función para ir a la página siguiente
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Resetear página al cambiar de tab
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Función para renderizar acciones contextuales según el estado
  const renderActions = (plan) => {
    // Solo mostrar acciones para solicitudes pendientes
    if (plan.status === 'pending') {
      return (
        <>
          <button onClick={() => showConfirmationDialog('approve', plan.id, '¿Aprobar esta solicitud?')}
                  className="hover:text-green-300 text-green-400 transition-colors duration-200 flex items-center gap-2">
            <Check className="h-4 w-4" />
            <span>Aprobar</span>
          </button>
          <button onClick={() => showConfirmationDialog('reject', plan.id, '¿Rechazar esta solicitud?')}
                  className="hover:text-red-300 text-red-400 transition-colors duration-200 flex items-center gap-2">
            <X className="h-4 w-4" />
            <span>Rechazar</span>
          </button>
        </>
      );
    }
    // No mostrar acciones para los demás estados
    return null;
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Pestañas para filtrar por estado */}
      <div className="border-b border-gray-700">
        <nav className="flex space-x-8 mt-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('all')}
            className={`${
              activeTab === 'all'
                ? 'border-blue-500 text-blue-500'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
            } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
          >
            Todas
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`${
              activeTab === 'pending'
                ? 'border-yellow-500 text-yellow-500'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
            } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
          >
            Pendientes
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`${
              activeTab === 'active'
                ? 'border-green-500 text-green-500'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
            } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
          >
            Activas
          </button>
          <button
            onClick={() => setActiveTab('rejected')}
            className={`${
              activeTab === 'rejected'
                ? 'border-red-500 text-red-500'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
            } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
          >
            Rechazadas
          </button>
        </nav>
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-white sm:pl-0">
                    Usuario
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-white">
                    Rol
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-white">
                    Plan Solicitado
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-white">
                    Fecha
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                    <span className="sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-900 divide-y divide-gray-700">
                {currentItems.length > 0 ? (
                  currentItems.map(plan => (
                    <tr key={plan.id} className="hover:bg-gray-700/50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-700 flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-400" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{plan.userEmail}</div>
                            <div className="text-xs text-gray-400">Nombre: {plan.userName}</div>
                            <div className="text-xs text-gray-400">Solicitud: {plan.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border bg-blue-900/50 text-blue-400 border-blue-400/30">
                          {plan.userRole || 'Usuario'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">Plan ID: {plan.subscriptionPlanId}</div>
                        <div className={`text-xs ${
                          plan.status === 'active' ? 'text-green-400' : 
                          plan.status === 'rejected' ? 'text-red-400' : 
                          plan.status === 'pending' ? 'text-yellow-400' : 
                          'text-gray-400'}`
                        }>
                          Estado: {plan.status}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {new Date(plan.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-4">
                          {/* Renderizar acciones contextuales según estado */}
                          {renderActions(plan)}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-400">
                      No hay solicitudes {activeTab !== 'all' ? `con estado "${activeTab}"` : ''}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Paginación */}
      {filteredPlans.length > itemsPerPage && (
        <div className="px-4 py-3 flex items-center justify-between border-t border-gray-700 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md ${
                currentPage === 1 
                ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Anterior
            </button>
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md ${
                currentPage === totalPages 
                ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Siguiente
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a{' '}
                <span className="font-medium">
                  {indexOfLastItem > filteredPlans.length ? filteredPlans.length : indexOfLastItem}
                </span>{' '}
                de <span className="font-medium">{filteredPlans.length}</span> resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-700 bg-gray-800 text-sm font-medium ${
                    currentPage === 1 
                    ? 'text-gray-500 cursor-not-allowed' 
                    : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <span className="sr-only">Anterior</span>
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {/* Números de página */}
                {[...Array(totalPages)].map((_, i) => {
                  const pageNumber = i + 1;
                  // Mostrar página actual, primera, última y 1 página antes y después de la actual
                  if (
                    pageNumber === 1 || 
                    pageNumber === totalPages || 
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 border ${
                          pageNumber === currentPage
                            ? 'bg-blue-700 text-white border-blue-500 z-10'
                            : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                        } text-sm font-medium`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                  
                  // Mostrar elipsis para páginas omitidas
                  if (
                    (pageNumber === 2 && currentPage > 3) ||
                    (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                  ) {
                    return (
                      <span
                        key={`ellipsis-${pageNumber}`}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-700 bg-gray-800 text-gray-300 text-sm font-medium"
                      >
                        ...
                      </span>
                    );
                  }
                  
                  return null;
                })}
                
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-700 bg-gray-800 text-sm font-medium ${
                    currentPage === totalPages 
                    ? 'text-gray-500 cursor-not-allowed' 
                    : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <span className="sr-only">Siguiente</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para acciones */}
      {confirmAction && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-white">
                    Confirmar acción
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-300">
                      {confirmAction.message}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm"
                  onClick={executeAction}
                  disabled={processing}
                >
                  {processing ? 'Procesando...' : 'Confirmar'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-700 text-base font-medium text-gray-300 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setConfirmAction(null)}
                  disabled={processing}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersWhoWantToSubscribe;